import { FC, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Button from "../../components/Button";
import Modal from "../../components/Modal";
import { AuthContext } from "../AuthContext";
import { usePostLoginModal } from "../PostLoginModalContext";
import {
    createFundingStatus,
    FundingStatusItem,
    getFundingStatusHistory,
    getFundingStatusReminder,
} from "../../services/api/fundingStatus";
import { isFundingReminderEligibleRole } from "../../utils/graduation";
import styles from "./styles.module.scss";

const FundingReminderProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, hasPermission } = useContext(AuthContext)!;
    const { currentModal, requestModal, dismissModal } = usePostLoginModal();

    const [fundingStatus, setFundingStatus] = useState<FundingStatusItem | null>(null);
    const [statusLoading, setStatusLoading] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);

    const isOpen = currentModal === "fundingReminder";

    /** Vérifie à chaque changement de page si une mise à jour du statut est recommandée (sauf sur la page Payouts). Expert+ uniquement. */
    useEffect(() => {
        if (!user || !hasPermission("funding-status", "read")) return;
        if (!isFundingReminderEligibleRole(user.role)) return;
        if (location.pathname === "/payouts") return;

        getFundingStatusReminder(user.id)
            .then((res) => {
                if (res?.needsUpdate) {
                    requestModal("fundingReminder");
                }
            })
            .catch(() => {});
    }, [user, hasPermission, requestModal, location.pathname]);

    useEffect(() => {
        if (!isOpen || !user) return;

        setStatusLoading(true);
        getFundingStatusHistory(user.id, { limit: 1, sort: "asOfDate:desc" })
            .then((status) => {
                setFundingStatus(status?.items?.[0] ?? null);
            })
            .catch(() => {
                setFundingStatus(null);
            })
            .finally(() => setStatusLoading(false));
    }, [isOpen, user]);

    const handleClose = useCallback(() => {
        dismissModal();
    }, [dismissModal]);

    const handleConfirmNoChange = useCallback(async () => {
        if (!user) return;

        const isFunded = fundingStatus?.isFunded ?? false;
        const fundedAccountsCount = fundingStatus?.fundedAccountsCount ?? 0;
        const fundedTotalBalance = Number(fundingStatus?.fundedTotalBalance ?? 0);

        setConfirmLoading(true);
        try {
            await createFundingStatus(user.id, {
                isFunded,
                fundedAccountsCount,
                fundedTotalBalance,
            });
            toast.success("Statut financé confirmé (aucun changement).");
            handleClose();
        } catch {
            toast.error(
                "Une erreur s'est produite lors de la confirmation du statut."
            );
        } finally {
            setConfirmLoading(false);
        }
    }, [user, fundingStatus, handleClose]);

    const handleGoUpdate = useCallback(() => {
        dismissModal();
        navigate("/payouts?openFundingModal=1");
    }, [dismissModal, navigate]);

    const lastUpdateLabel =
        fundingStatus?.asOfDate &&
        new Date(fundingStatus.asOfDate).toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });

    const balanceFormatted =
        fundingStatus?.isFunded &&
        new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(
            Number(fundingStatus?.fundedTotalBalance || "0")
        );

    return (
        <>
            <Modal
                open={isOpen}
                onClose={handleClose}
                size="small"
                className={styles.modalTransparent}
                canClose={false}
            >
                <div className={styles.wrapper}>
                    <div className={styles.content}>
                        <div className={styles.header}>
                            <div className={styles.iconWrap}>🏆</div>
                            <h2 className={styles.title}>
                                Mise à jour de mon statut de trader financé
                            </h2>
                        </div>
                        <p className={styles.message}>
                            Pour garder un suivi pédagogique précis, confirmez
                            votre statut actuel ou mettez-le à jour.
                        </p>

                        <div className={styles.summary}>
                            {statusLoading ? (
                                <div className={styles.summaryLoading}>
                                    <span className={styles.summaryLoadingDot} />
                                    <span className={styles.summaryLoadingDot} />
                                    <span className={styles.summaryLoadingDot} />
                                </div>
                            ) : fundingStatus ? (
                                <>
                                    <div
                                        className={`${styles.statusBadge} ${
                                            fundingStatus.isFunded
                                                ? styles.statusBadgeFunded
                                                : styles.statusBadgeNotFunded
                                        }`}
                                    >
                                        {fundingStatus.isFunded
                                            ? "Je suis financé"
                                            : "Je ne suis pas financé"}
                                    </div>
                                    <div className={styles.summaryGrid}>
                                        <div className={styles.summaryRow}>
                                            <span className={styles.summaryLabel}>
                                                Nb de comptes financés
                                            </span>
                                            <span className={styles.summaryValue}>
                                                {fundingStatus.fundedAccountsCount ?? 0}
                                            </span>
                                        </div>
                                        <div className={styles.summaryRow}>
                                            <span className={styles.summaryLabel}>
                                                Capital total (capital du compte + PnL)
                                            </span>
                                            <span
                                                className={`${styles.summaryValue} ${fundingStatus.isFunded ? styles.summaryValueHighlight : ""}`}
                                            >
                                                {fundingStatus.isFunded
                                                    ? `${balanceFormatted} $`
                                                    : "0"}
                                            </span>
                                        </div>
                                        {lastUpdateLabel && (
                                            <div className={styles.summaryRow}>
                                                <span className={styles.summaryLabel}>
                                                    Dernière MAJ
                                                </span>
                                                <span className={styles.summaryValue}>
                                                    {lastUpdateLabel}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div
                                        className={`${styles.statusBadge} ${styles.statusBadgeNotFunded}`}
                                    >
                                        Je ne suis pas financé
                                    </div>
                                    <div className={styles.summaryGrid}>
                                        <div className={styles.summaryRow}>
                                            <span className={styles.summaryLabel}>
                                                Nb de comptes financés
                                            </span>
                                            <span className={styles.summaryValue}>
                                                0
                                            </span>
                                        </div>
                                        <div className={styles.summaryRow}>
                                            <span className={styles.summaryLabel}>
                                                Capital total (capital du compte + PnL)
                                            </span>
                                            <span className={styles.summaryValue}>
                                                0
                                            </span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {!fundingStatus && !statusLoading && (
                            <p className={styles.emptyText}>
                                Vous n'avez pas encore renseigné votre statut financé.
                            </p>
                        )}

                        <div className={styles.ctas}>
                            <Button
                                size="newSmall"
                                variant="secondary"
                                label={
                                    fundingStatus
                                        ? "Aucun changement"
                                        : "Je ne suis pas financé"
                                }
                                onClick={handleConfirmNoChange}
                                className={styles.cta}
                                disabled={statusLoading}
                                isLoading={confirmLoading}
                            />
                            <Button
                                size="newSmall"
                                variant="primary"
                                label={
                                    fundingStatus
                                        ? "Ma situation a évolué"
                                        : "Je suis financé"
                                }
                                onClick={handleGoUpdate}
                                className={`${styles.cta} ${styles.ctaPrimary}`}
                                disabled={statusLoading}
                            />
                        </div>
                    </div>
                </div>
            </Modal>
            {children}
        </>
    );
};

export default FundingReminderProvider;
